import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Blocks, Plus, Loader2, Trash2, Pencil, X, Gamepad2, BookOpen, HelpCircle, Grid3x3, DoorClosed, ArrowUp, ArrowDown, Play, GraduationCap, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError, uploadImage, mediaUrl } from "../../lib/api";
import GameConfigEditor from "../../components/os/games/GameConfigEditor";

const ease = [0.16, 1, 0.3, 1];
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";
const ROLE_KEYS = ["ceo", "ventas", "cuentas", "produccion", "disenador", "fabricante", "cliente"];
const ROLE_LABELS = { ceo: "Dirección", ventas: "Ventas", cuentas: "Cuentas", produccion: "Producción", disenador: "Diseño", fabricante: "Fabricante", cliente: "Cliente" };

const GAME_META = {
  trivia: { label: "Trivia", icon: HelpCircle, color: "text-violet", bg: "bg-violet/10", desc: "Preguntas de opción múltiple con puntos y temporizador" },
  memorama: { label: "Memorama", icon: Grid3x3, color: "text-emerald-600", bg: "bg-emerald-500/10", desc: "Empareja concepto ↔ definición volteando cartas" },
  escape: { label: "Escape Room", icon: DoorClosed, color: "text-gold", bg: "bg-gold/15", desc: "Serie de acertijos que se desbloquean uno a uno" },
};
const emptyCfg = (t) => t === "trivia" ? { seconds_per_q: 0, questions: [] } : t === "memorama" ? { pairs: [] } : { intro: "", success: "", stages: [] };

function RolePicker({ roles, onToggle, testid }) {
  return (
    <div>
      <label className="text-xs text-navy/50 block mb-1.5">¿Quién puede verlo? <span className="text-navy/35">(ninguno = todos)</span></label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {ROLE_KEYS.map((r) => (
          <button key={r} type="button" onClick={() => onToggle(r)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${roles.includes(r) ? "bg-violet text-white border-violet" : "border-navy/15 text-navy/60 hover:border-navy/30"}`}>{ROLE_LABELS[r]}</button>
        ))}
      </div>
    </div>
  );
}

export default function ContentAuthoring() {
  const [tab, setTab] = useState("courses");
  const [games, setGames] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canAuthor, setCanAuthor] = useState(false);
  const [gameModal, setGameModal] = useState(null);   // {} new or existing
  const [courseModal, setCourseModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/authoring/permissions").then((r) => setCanAuthor(!!r.data.can_author)).catch(() => {});
    Promise.all([
      api.get("/authoring/games").catch(() => ({ data: [] })),
      api.get("/authoring/courses").catch(() => ({ data: [] })),
    ]).then(([g, c]) => { setGames(g.data); setCourses(c.data); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const delGame = async (id) => { if (!window.confirm("¿Eliminar este juego?")) return; try { await api.delete(`/authoring/games/${id}`); toast.success("Juego eliminado"); load(); } catch { toast.error("Error"); } };
  const delCourse = async (id) => { if (!window.confirm("¿Eliminar este curso?")) return; try { await api.delete(`/authoring/courses/${id}`); toast.success("Curso eliminado"); load(); } catch { toast.error("Error"); } };

  return (
    <OSLayout title="OMNIA Content Authoring">
      <div className="space-y-6" data-testid="authoring-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-violet"><Blocks className="w-5 h-5" /><span className="overline text-navy/40">Constructor de contenido</span></div>
            <h1 className="text-3xl font-medium tracking-tight mt-1">Constructor de Cursos y Juegos</h1>
            <p className="text-navy/50 mt-1 text-sm max-w-2xl">Crea cursos interactivos y juegos (trivia, memorama, escape room). Aparecen en OMNIA Academy para tu equipo.</p>
          </div>
          <Link to="/os/academia" className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-5 py-3 hover:bg-navy/5 transition-colors"><GraduationCap className="w-4 h-4" /> Ver en Academy</Link>
        </div>

        <div className="flex items-center gap-1 border-b border-navy/10">
          {[["courses", `Cursos (${courses.length})`], ["games", `Juegos (${games.length})`]].map(([k, l]) => (
            <button key={k} data-testid={`authoring-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-violet text-navy font-medium" : "border-transparent text-navy/50 hover:text-navy"}`}>{l}</button>
          ))}
          <div className="ml-auto pb-2">
            {!canAuthor ? null : tab === "games"
              ? <button data-testid="new-game-btn" onClick={() => setGameModal({ type: "trivia", title: "", description: "", roles: [], config: emptyCfg("trivia") })} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90"><Plus className="w-4 h-4" /> Nuevo juego</button>
              : <button data-testid="new-course-btn" onClick={() => setCourseModal({ title: "", description: "", roles: [], cover: "", blocks: [] })} className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90"><Plus className="w-4 h-4" /> Nuevo curso</button>}
          </div>
        </div>

        {!canAuthor && !loading && (
          <div className="bg-gold/10 border border-gold/25 rounded-xl px-4 py-3 text-sm text-navy/70" data-testid="authoring-readonly">
            Solo admin y líderes/RH pueden crear o editar. Puedes jugar el contenido desde OMNIA Academy.
          </div>
        )}

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          tab === "games" ? (
            games.length === 0 ? <Empty label="Aún no has creado juegos." /> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {games.map((g) => {
                  const M = GAME_META[g.type] || GAME_META.trivia; const Icon = M.icon;
                  return (
                    <div key={g.id} data-testid={`game-card-${g.id}`} className="bg-white border border-navy/10 rounded-xl p-5 flex flex-col group">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${M.bg} ${M.color}`}><Icon className="w-3.5 h-3.5" /> {M.label}</span>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button data-testid={`game-edit-${g.id}`} onClick={() => openGame(g, setGameModal)} className="text-navy/30 hover:text-violet"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => delGame(g.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <h3 className="mt-3 font-medium text-lg leading-snug">{g.title}</h3>
                      {g.description && <p className="mt-1 text-sm text-navy/50 line-clamp-2">{g.description}</p>}
                      <div className="mt-4 pt-3 border-t border-navy/5 flex items-center justify-between">
                        <Link to={`/os/academia/juego/${g.id}`} data-testid={`game-play-${g.id}`} className="text-sm text-violet hover:underline inline-flex items-center gap-1"><Play className="w-3.5 h-3.5" /> Jugar</Link>
                        {!g.ready && <span className="text-[11px] text-gold">Incompleto</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            courses.length === 0 ? <Empty label="Aún no has creado cursos interactivos." /> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map((c) => (
                  <div key={c.id} data-testid={`ac-card-${c.id}`} className="bg-white border border-navy/10 rounded-xl overflow-hidden flex flex-col group">
                    {c.cover ? <img src={mediaUrl(c.cover)} alt="" className="h-28 w-full object-cover" /> : <div className="h-28 bg-gradient-to-br from-navy to-violet/60" />}
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-medium text-lg leading-snug">{c.title}</h3>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                          <button data-testid={`ac-edit-${c.id}`} onClick={() => openCourse(c, setCourseModal)} className="text-navy/30 hover:text-violet"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => delCourse(c.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                      {c.description && <p className="mt-1 text-sm text-navy/50 line-clamp-2">{c.description}</p>}
                      <div className="mt-2 text-xs text-navy/40">{c.blocks} bloques · {c.lessons} lecciones · {c.games} juegos</div>
                      <div className="mt-4 pt-3 border-t border-navy/5">
                        <Link to={`/os/academia/interactivo/${c.id}`} data-testid={`ac-open-${c.id}`} className="text-sm text-violet hover:underline">Abrir curso →</Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )
        )}
      </div>
      {gameModal && <GameModal data={gameModal} onClose={() => setGameModal(null)} onSaved={() => { setGameModal(null); load(); }} />}
      {courseModal && <CourseModal data={courseModal} onClose={() => setCourseModal(null)} onSaved={() => { setCourseModal(null); load(); }} />}
    </OSLayout>
  );
}

const Empty = ({ label }) => <div className="text-navy/40 py-16 text-center border border-dashed border-navy/15 rounded-lg" data-testid="authoring-empty">{label}</div>;

async function openGame(g, setGameModal) {
  try { const { data } = await api.get(`/authoring/games/${g.id}`); setGameModal({ id: g.id, type: data.type, title: data.title, description: data.description, roles: data.roles || [], config: data.raw_config || emptyCfg(data.type) }); }
  catch { toast.error("No se pudo abrir el juego"); }
}
async function openCourse(c, setCourseModal) {
  try { const { data } = await api.get(`/authoring/courses/${c.id}`); setCourseModal({ id: c.id, title: data.title, description: data.description, roles: data.roles || [], cover: data.cover || "", blocks: data.raw_blocks || [] }); }
  catch { toast.error("No se pudo abrir el curso"); }
}

function GameModal({ data, onClose, onSaved }) {
  const [f, setF] = useState(data);
  const [busy, setBusy] = useState(false);
  const toggleRole = (r) => setF((p) => ({ ...p, roles: p.roles.includes(r) ? p.roles.filter((x) => x !== r) : [...p.roles, r] }));
  const setType = (t) => setF((p) => ({ ...p, type: t, config: emptyCfg(t) }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Escribe un título"); return; }
    setBusy(true);
    const body = { type: f.type, title: f.title, description: f.description, roles: f.roles, config: f.config };
    try {
      if (f.id) await api.patch(`/authoring/games/${f.id}`, body); else await api.post("/authoring/games", body);
      toast.success("Juego guardado"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="game-modal" title={f.id ? "Editar juego" : "Nuevo juego"}>
      {!f.id && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {Object.entries(GAME_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button key={k} data-testid={`game-type-${k}`} onClick={() => setType(k)}
                className={`rounded-xl border p-3 text-left transition-colors ${f.type === k ? "border-violet bg-violet/5" : "border-navy/10 hover:border-navy/25"}`}>
                <Icon className={`w-5 h-5 ${m.color}`} />
                <div className="text-sm font-medium mt-1.5">{m.label}</div>
                <div className="text-[11px] text-navy/45 mt-0.5 leading-tight">{m.desc}</div>
              </button>
            );
          })}
        </div>
      )}
      <div className="space-y-3">
        <input data-testid="game-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Título del juego" className={input} />
        <textarea data-testid="game-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} placeholder="Descripción (opcional)" className={input} />
        <RolePicker roles={f.roles} onToggle={toggleRole} testid="game-roles" />
        <div className="border-t border-navy/10 pt-3">
          <GameConfigEditor type={f.type} config={f.config} onChange={(config) => setF({ ...f, config })} />
        </div>
      </div>
      <button data-testid="game-save" onClick={save} disabled={busy} className="mt-5 w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />} Guardar juego
      </button>
    </Modal>
  );
}

function CourseModal({ data, onClose, onSaved }) {
  const [f, setF] = useState(data);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const toggleRole = (r) => setF((p) => ({ ...p, roles: p.roles.includes(r) ? p.roles.filter((x) => x !== r) : [...p.roles, r] }));
  const uid = () => Math.random().toString(36).slice(2, 10);
  const addBlock = (type) => setF((p) => ({ ...p, blocks: [...p.blocks, type === "lesson" ? { id: uid(), type, title: "", content: "", image: "" } : { id: uid(), type, title: "", config: emptyCfg(type) }] }));
  const setBlock = (i, patch) => setF((p) => ({ ...p, blocks: p.blocks.map((b, j) => j === i ? { ...b, ...patch } : b) }));
  const rmBlock = (i) => setF((p) => ({ ...p, blocks: p.blocks.filter((_, j) => j !== i) }));
  const move = (i, d) => setF((p) => { const a = [...p.blocks]; const j = i + d; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return { ...p, blocks: a }; });
  const onCover = async (e) => { const file = e.target.files?.[0]; if (!file) return; setUploading(true); try { const url = await uploadImage(file); setF((p) => ({ ...p, cover: url })); } catch { toast.error("No se pudo subir la portada"); } finally { setUploading(false); } };
  const onBlockImage = async (i, e) => { const file = e.target.files?.[0]; if (!file) return; try { const url = await uploadImage(file); setBlock(i, { image: url }); } catch { toast.error("No se pudo subir la imagen"); } };
  const save = async () => {
    if (!f.title.trim()) { toast.error("Escribe el título del curso"); return; }
    if (!f.blocks.length) { toast.error("Agrega al menos un bloque"); return; }
    setBusy(true);
    const body = { title: f.title, description: f.description, roles: f.roles, cover: f.cover, blocks: f.blocks };
    try {
      if (f.id) await api.patch(`/authoring/courses/${f.id}`, body); else await api.post("/authoring/courses", body);
      toast.success("Curso guardado"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="course-modal" title={f.id ? "Editar curso" : "Nuevo curso interactivo"} wide>
      <div className="space-y-3">
        <input data-testid="course-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Título del curso" className={input} />
        <textarea data-testid="course-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} placeholder="Descripción" className={input} />
        <div className="flex items-center gap-3">
          {f.cover ? <img src={mediaUrl(f.cover)} alt="" className="w-24 h-14 object-cover rounded-lg border border-navy/10" /> : <div className="w-24 h-14 rounded-lg bg-navy/5 flex items-center justify-center text-navy/30"><ImageIcon className="w-5 h-5" /></div>}
          <label className="btn-ghost cursor-pointer text-sm rounded-full border border-navy/15 px-4 py-2 hover:bg-navy/5 inline-flex items-center gap-2" data-testid="course-cover-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} {f.cover ? "Cambiar portada" : "Subir portada"}
            <input type="file" accept="image/*" className="hidden" onChange={onCover} />
          </label>
          {f.cover && <button onClick={() => setF({ ...f, cover: "" })} className="text-xs text-navy/40 hover:text-destructive">Quitar</button>}
        </div>
        <RolePicker roles={f.roles} onToggle={toggleRole} testid="course-roles" />

        <div className="border-t border-navy/10 pt-3">
          <div className="text-sm font-medium mb-2">Bloques del curso ({f.blocks.length})</div>
          <div className="space-y-3" data-testid="course-blocks">
            {f.blocks.map((b, i) => {
              const M = b.type === "lesson" ? { label: "Lección", icon: BookOpen, color: "text-navy/60", bg: "bg-navy/5" } : (GAME_META[b.type] || {});
              const Icon = M.icon || BookOpen;
              return (
                <div key={b.id} className="border border-navy/10 rounded-xl p-3 bg-cream/40" data-testid={`course-block-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full ${M.bg} ${M.color}`}><Icon className="w-3.5 h-3.5" /> {M.label}</span>
                    <input value={b.title} onChange={(e) => setBlock(i, { title: e.target.value })} placeholder="Título del bloque" className={`${input} flex-1`} data-testid={`block-title-${i}`} />
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="text-navy/30 hover:text-navy disabled:opacity-20"><ArrowUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === f.blocks.length - 1} className="text-navy/30 hover:text-navy disabled:opacity-20"><ArrowDown className="w-4 h-4" /></button>
                    <button onClick={() => rmBlock(i)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {b.type === "lesson" ? (
                    <div className="space-y-2">
                      <textarea value={b.content} onChange={(e) => setBlock(i, { content: e.target.value })} rows={5} placeholder="Contenido de la lección (Markdown: # título, ## subtítulo, - lista)" className={`${input} font-mono leading-relaxed`} data-testid={`block-content-${i}`} />
                      <div className="flex items-center gap-3">
                        {b.image ? <img src={mediaUrl(b.image)} alt="" className="w-20 h-12 object-cover rounded border border-navy/10" /> : null}
                        <label className="cursor-pointer text-xs text-violet hover:underline inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {b.image ? "Cambiar imagen" : "Añadir imagen"}<input type="file" accept="image/*" className="hidden" onChange={(e) => onBlockImage(i, e)} /></label>
                        {b.image && <button onClick={() => setBlock(i, { image: "" })} className="text-xs text-navy/40 hover:text-destructive">Quitar</button>}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-3 border border-navy/10">
                      <GameConfigEditor type={b.type} config={b.config} onChange={(config) => setBlock(i, { config })} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {[["lesson", "Lección", BookOpen], ["trivia", "Trivia", HelpCircle], ["memorama", "Memorama", Grid3x3], ["escape", "Escape Room", DoorClosed]].map(([t, l, Icon]) => (
              <button key={t} data-testid={`add-block-${t}`} onClick={() => addBlock(t)} className="text-xs rounded-full border border-dashed border-navy/25 px-3 py-1.5 text-navy/60 hover:border-violet hover:text-violet inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> + {l}</button>
            ))}
          </div>
        </div>
      </div>
      <button data-testid="course-save" onClick={save} disabled={busy} className="mt-5 w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Blocks className="w-4 h-4" />} Guardar curso
      </button>
    </Modal>
  );
}

function Modal({ children, onClose, title, testid, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()}
        data-testid={testid} className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} bg-cream rounded-xl p-6 max-h-[90vh] overflow-y-auto thin-scroll`}>
        <div className="flex items-center justify-between mb-4"><h3 className="text-xl font-medium">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </motion.div>
    </div>
  );
}
